import React from 'react';
import { ScrollMenu } from 'react-horizontal-scrolling-menu';
import './sub-sections-filters-chips.scss';
import VideoHlroomAnalytics from '@sky-it-video/hlroom-analytics';

import { IClassNames, makeClassNames } from '../../../utils/styles';
import useDrag from '../../../hooks/useDrag';
import ChipFilterImage from '../../atoms/chip-filter-image/chip-filter-image';
import { ISubSection, ISubSubSection } from '../../../typings/hlroom';

export interface ISubSectionsFiltersChips {
  isDesktop: boolean;
  subSectionSelected: ISubSection;
  subSubSectionSelected?: ISubSubSection;
  onSubSubSectionChange: (subSubSection: ISubSubSection) => void;
  appType?: string;
}

const SubSectionsFiltersChips: React.FC<ISubSectionsFiltersChips> = ({
  isDesktop,
  subSectionSelected,
  subSubSectionSelected,
  onSubSubSectionChange,
  appType = 'sport',
}) => {
  const { dragStart, dragStop, dragMove, dragging } = useDrag();

  const handleDrag =
    ({ scrollContainer }: any) =>
    (ev: React.MouseEvent) =>
      dragMove(ev, (posDiff) => {
        if (scrollContainer.current) {
          scrollContainer.current.scrollLeft += posDiff;
        }
      });

  const showFilterSubSections = () => isDesktop && Boolean(subSectionSelected.tag);

  const classNames = (): IClassNames =>
    makeClassNames({
      subSectionsFiltersChips: [
        'sub-sections-filters-chips',
        !isDesktop &&
          subSectionSelected.subSubSections.length > 0 &&
          subSubSectionSelected === undefined &&
          'sub-sections-filters-chips--open',
        showFilterSubSections() && 'sub-sections-filters-chips__desktop',
      ],
      subSectionsFiltersChipsSliderContainer: 'sub-sections-filters-chips__container',
      subSectionsFiltersChipsWrapper: [
        'sub-sections-filters-chips__wrapper',
        showFilterSubSections() && 'sub-sections-filters-chips__wrapper__desktop',
      ],
    });
  const cn = classNames();

  const clickOnChip = (tag: string) => {
    if (dragging) {
      return false;
    }

    if (subSectionSelected.subSubSections.length) {
      const subSubSectionSelected = subSectionSelected.subSubSections.find((s) => s.tag === tag);
      if (subSubSectionSelected) {
        VideoHlroomAnalytics.setDigitalData(
          {
            zoneid: {
              subSection: subSectionSelected?.tag?.replace(' ', '-'),
              subSubSection: subSubSectionSelected?.tag.replace(' ', '-'),
            },
          },
          appType
        );
        onSubSubSectionChange(subSubSectionSelected);
      }
    }

    return false;
  };

  const desktopTemplate = () => (
    <div className={cn?.subSectionsFiltersChipsWrapper}>
      {subSectionSelected.subSubSections.length > 0 &&
        subSectionSelected.subSubSections
          .filter((subSubSection) => subSubSection.top)
          .map((s, i) => (
            <ChipFilterImage
              itemId={i}
              selectChip={(i) => clickOnChip(s.tag)}
              key={s.tag + i}
              text={s.name}
              isSelected={subSubSectionSelected?.tag === s.tag}
              isDesktop={isDesktop}
              images={s.images}
            />
          ))}
    </div>
  );

  const mobileTemplate = () =>
    !isDesktop &&
    subSectionSelected.subSubSections.length > 0 &&
    Boolean(!subSubSectionSelected) && (
      <ScrollMenu
        scrollContainerClassName={cn?.subSectionsFiltersChipsSliderContainer}
        onMouseDown={() => dragStart}
        onMouseUp={() => dragStop}
        onMouseMove={handleDrag}
        wrapperClassName={cn?.subSectionsFiltersChipsWrapper}
      >
        {subSectionSelected.subSubSections
          .filter((subSubSection) => subSubSection.top)
          .map((s, i) => (
            <ChipFilterImage
              itemId={i}
              selectChip={(i) => clickOnChip(s.tag)}
              key={s.tag + i}
              text={s.name}
              isSelected={subSubSectionSelected?.tag === s.tag}
              isDesktop={isDesktop}
              images={s.images}
            />
          ))}
      </ScrollMenu>
    );

  return (
    <div className={cn?.subSectionsFiltersChips} onMouseLeave={dragStop}>
      {showFilterSubSections() ? desktopTemplate() : mobileTemplate()}
    </div>
  );
};

export default SubSectionsFiltersChips;
